<!-- collectionOperationDashboard/component/UfPerformance.vue -->
<template>
  <div class="card uf-card">
    <div class="card-head">
      <div>
        <h3>{{ $t('collectionOperationDashboard.ufTitle') }}</h3>
        <p>{{ $t('collectionOperationDashboard.ufDesc') }}</p>
      </div>
    </div>
    <div ref="ufChart" class="chart tall" />
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { getFmCollectUfChart } from '../api';
import { formatAxisCount, rateAxisBounds, rateOf } from '../utils/format';

export default {
  name: 'UfPerformance',
  props: {
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      chart: null,
      ufRows: []
    };
  },
  watch: {
    // 切换语言不会刷新页面，图表系列名需按新语言重绘
    '$i18n.locale'() {
      this.updateChart();
    }
  },
  mounted() {
    this.initChart();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    if (this.chart) {
      this.chart.dispose();
    }
  },
  methods: {
    getBody(res) {
      if (res && typeof res === 'object' && Object.prototype.hasOwnProperty.call(res, 'data')) {
        return res.data;
      }
      return res;
    },

    // 供父级“查询”时统一调用
    fetchData(params) {
      return this.loadUfChart(params);
    },

    async loadUfChart(params) {
      try {
        const res = await getFmCollectUfChart({
          startDate: params.startDate,
          endDate: params.endDate,
          platform: params.platform,
          serviceType: params.serviceType,
          stateCode: params.stateCode,
          cityName: params.cityName,
          siteIds: params.siteIds
        });
        console.log('[UfPerformance] 按UF统计 ufChart 响应：', res);
        const body = this.getBody(res);
        const list = Array.isArray(body)
          ? body
          : body && Array.isArray(body.list)
            ? body.list
            : [];
        this.ufRows = list;
        this.updateChart();
      } catch (error) {
        console.error('[UfPerformance] 按UF统计加载失败：', error);
      }
    },

    formatNumber(num) {
      return new Intl.NumberFormat('zh-CN').format(Math.round(num));
    },

    // 双轴 tooltip：揽收量千分位、揽收率保留 2 位
    buildTooltip(params) {
      const list = Array.isArray(params) ? params : [params];
      if (!list.length) return '';
      const lines = list.map((item) => {
        const isRate = item.seriesName === '揽收率';
        const value =
          item.value === null || item.value === undefined
            ? '—'
            : isRate
              ? `${Number(item.value).toFixed(2)}%`
              : this.formatNumber(item.value);
        return `${item.marker}${item.seriesName}：${value}`;
      });
      return [list[0].axisValue, ...lines].join('<br/>');
    },

    initChart() {
      this.chart = echarts.init(this.$refs.ufChart);
      this.updateChart();
    },

    updateChart() {
      const rows = Array.isArray(this.ufRows) ? this.ufRows : [];
      // UNKNOWN = 发件州缺失的兜底桶，展示成「未知」而不是裸露英文
      const unknownLabel = this.$t('collectionOperationDashboard.unknownState');
      const names = rows.map((item) => (item.stateCode === 'UNKNOWN' ? unknownLabel : item.stateCode || ''));
      const volumes = rows.map((item) => Number(item.collectTotal) || 0);
      const rates = rows.map((item) => {
        // 优先按分子/分母重算，后端 collectRate 只有 1 位小数
        const computed = rateOf(item.loadedTotal, item.collectTotal);
        if (computed !== null) return computed;
        const v = item.collectRate;
        return v === '' || v === null || v === undefined ? null : Number(v);
      });
      const rateAxis = rateAxisBounds(rates);

      const option = {
        animationDuration: 720,
        animationEasing: 'cubicOut',
        color: ['#079455', '#2f6fed'],
        textStyle: {
          fontFamily: 'Inter, PingFang SC, Microsoft YaHei, sans-serif',
          color: '#52665c'
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(16,35,27,.96)',
          borderColor: 'rgba(255,255,255,.12)',
          borderWidth: 1,
          padding: [10, 12],
          textStyle: { color: '#fff', fontSize: 12 },
          axisPointer: {
            type: 'line',
            lineStyle: { color: '#91cdb0', type: 'dashed' }
          },
          confine: true,
          // 默认会把重算后的揽收率原始浮点（32.08955223880597）直出，这里统一格式化
          formatter: (params) => this.buildTooltip(params)
        },
        legend: { top: 8 },
        grid: {
          left: 55,
          right: 52,
          top: 45,
          bottom: 46
        },
        xAxis: [
          {
            type: 'value',
            // 量级小于 1000 时直接显示原值，避免全被抹成 0k（2026-10-26 测试反馈）
            // minInterval=1：数量轴必须是整数步长，否则 0~3 这种小范围会出现 0.5 步长，
            // 取整后变成 1 1 / 2 2 / 3 3 的重复刻度
            minInterval: 1,
            axisLabel: { formatter: (v) => formatAxisCount(v) }
          },
          {
            type: 'value',
            // 上下界取整到 1 位：原实现 minRate - 3 会算出 1.6700000000000002 并渲染成刻度
            min: rateAxis.min,
            max: rateAxis.max,
            axisLabel: { formatter: (v) => `${Math.round(v * 10) / 10}%` }
          }
        ],
        yAxis: {
          type: 'category',
          inverse: true,
          data: names
        },
        series: [
          {
            name: this.$t('collectionOperationDashboard.collectVolume'),
            type: 'bar',
            barMaxWidth: 19,
            showBackground: true,
            backgroundStyle: {
              color: '#f2f6f4',
              borderRadius: 6
            },
            data: volumes,
            itemStyle: {
              borderRadius: [0, 6, 6, 0],
              color: '#079455'
            }
          },
          {
            name: this.$t('collectionCenter.CollectionRate'),
            type: 'line',
            xAxisIndex: 1,
            data: rates,
            symbolSize: 7,
            lineStyle: { width: 2.5, color: '#2f6fed' },
            itemStyle: { color: '#2f6fed' }
          }
        ]
      };

      this.chart.setOption(option, true);
    },

    handleResize() {
      if (this.chart) {
        this.chart.resize();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.uf-card {
  border: 1px solid #e1e9e5;
  border-radius: 17px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 36, 27, 0.03), 0 12px 32px rgba(16, 36, 27, 0.055);
  min-width: 0;
  transition: 0.22s ease;
  margin-bottom: 12px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 2px 4px rgba(16, 36, 27, 0.04), 0 18px 42px rgba(16, 36, 27, 0.09);
    border-color: #d3e3db;
  }

  .card-head {
    padding: 16px 17px 3px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    h3 {
      font-size: 15px;
      margin: 0;
      letter-spacing: -0.1px;
    }

    p {
      font-size: 11px;
      color: #76877f;
      margin: 3px 0;
    }
  }

  .chart.tall {
    height: 380px;
  }
}
</style>
